import React, { useMemo } from 'react';
import { BsArrowRepeat } from 'react-icons/bs';
import { Box, CircularProgress, HStack, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PINSAIAssistantLabel from '../../../../../../../../components/PINSAIAssistantLabel';
import LoadingSpinner from '../../../../../../../../components/Spinner';
import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { GptRequestStatuses } from '../../../../../../../../constants/gptRequestStatuses';
import recordKeys from '../../../../../../../../services/models/record/keys';
import { useRecordGptRequests, useRecordQuestionRequirements } from '../../../../../../../../services/record-service';
import { getInfiniteListData } from '../../../../../../../../services/util';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import { queryClient } from '../../../../../../../../utils/queryClientUtil';
import { getDisplayValueForType } from '../../../../../../../../utils/tableUtil';

import QuestionRequirementTable from './QuestionRequirementTable';

const QuestionRequirementModal = ({ recordId, resourceType, resourceId, resourceDisplayName }) => {
  const { data: questions, isLoading: questionsLoading } = useRecordQuestionRequirements(recordId, { refetchOnMount: true });
  const { data: gptRequests, isLoading: gptRequestsLoading, isFetching: gptRequestsFetching } = useRecordGptRequests({
    recordId,
    options: { refetchOnMount: true }
  });

  const relatedQuestions = useMemo(() => {
    if (questionsLoading) {
      return [];
    }

    return questions.filter(question => question.links.find(link => link.resourceType === resourceType && link.resourceId === resourceId));
  }, [questions, questionsLoading, gptRequests]);

  const mostRecentGpt = useMemo(() => {
    if (gptRequestsLoading) {
      return null;
    }

    const data = getInfiniteListData(gptRequests);

    if (!data.length) {
      return null;
    }

    return data[0];
  }, [gptRequests, gptRequestsLoading]);

  const statusText = useMemo(() => {
    if (!mostRecentGpt?.status) return null;

    switch (mostRecentGpt.status) {
      case GptRequestStatuses.Processing:
        return 'New Answers in Progress...';

      case GptRequestStatuses.Published:
        return `Processed on ${getDisplayValueForType(mostRecentGpt.updatedAt, 'datetime')}`;

      case GptRequestStatuses.Completed: {
        if (!questions?.length) return null;

        // handle case if Completed status exists with published answers
        const answersHaveBeenPublished = questions.some(q => {
          if (!q?.answers?.length) return false;
          const latestAnswer = q.answers.at(-1);
          return latestAnswer?.gptRequestId === mostRecentGpt.id && !!latestAnswer?.publishedAt;
        });

        if (answersHaveBeenPublished) {
          return `Processed on ${getDisplayValueForType(mostRecentGpt.updatedAt, 'datetime')}`;
        }

        const lastPublishedGptRequest = getInfiniteListData(gptRequests).find(
          gptRequest => gptRequest.status === GptRequestStatuses.Published
        );

        return lastPublishedGptRequest
          ? `Processed on ${getDisplayValueForType(lastPublishedGptRequest.updatedAt, 'datetime')}`
          : null;
      }

      default:
        return null;
    }
  }, [mostRecentGpt, questions, relatedQuestions]);

  return (
    <Box
      data-cy={CypressIds.questionRequirementModal}
      overflowX="auto"
      overflowY="auto"
    >
      <PINSAIAssistantLabel mb={4} mt={2} textAlign="left" w="300px" />
      <HStack mb={6} spacing={8}>
        <Text
          color={colors.greyScale['800']}
          textAlign="left"
          textStyle="bodyXxl600"
        >
          {`Linked Questions (${relatedQuestions?.length}) for ${resourceDisplayName}`}
        </Text>
        {!!statusText && (
          <>
            <Text color={colors.greyScale['500']} textStyle="bodySm">{statusText}</Text>
            {mostRecentGpt?.status === GptRequestStatuses.Processing && (
              <>
                <CircularProgress isIndeterminate color={colors.primaryScale['600']} size="25px" />
                <Tooltip label="Refresh" placement="top">
                  <IconButton
                    aria-label="scan-progress-refresh"
                    icon={<Icon as={BsArrowRepeat} />}
                    size="sm"
                    variant="textPrimary"
                    onClick={() => {
                      queryClient.invalidateQueries(recordKeys.recordGptRequests(recordId));
                      queryClient.invalidateQueries(recordKeys.recordQuestionRequirements(recordId));
                    }}
                  />
                </Tooltip>
                {gptRequestsFetching && (
                  <Text color={colors.greyScale['500']} textStyle="bodySm">Fetching...</Text>
                )}
              </>
            )}
          </>
        )}
      </HStack>

      {(gptRequestsLoading || questionsLoading) ? (
        <Box h="200px" ht={8}>
          <LoadingSpinner isInline minHeight="100px" />
        </Box>
      ) : (
        <QuestionRequirementTable gptStatus={mostRecentGpt?.status} recordId={recordId} relatedQuestions={relatedQuestions} />
      )}
    </Box>
  );
};

QuestionRequirementModal.propTypes = {
  recordId: PropTypes.string.isRequired,
  resourceType: PropTypes.string.isRequired,
  resourceId: PropTypes.number.isRequired,
  resourceDisplayName: PropTypes.string.isRequired
};

export default QuestionRequirementModal;
